// useScannerAutoSubmit — tự gửi mã khi quét bằng máy, KHÔNG cần bấm Enter.
//
// Bối cảnh (Sếp yêu cầu 01/08): máy quét của cửa hàng không gửi phím Enter sau
// mỗi lần tít, nên nhân viên phải bấm Enter giữa các lần quét — quét cả lô rất mệt.
//
// Cách nhận biết "máy quét" thay vì gõ tay: máy quét bắn cả chuỗi trong vài chục
// mili-giây, người gõ tay chậm hơn nhiều. Chỉ tự gửi khi:
//   (a) phần lớn ký tự đến cách nhau dưới FAST_MS (nhập kiểu máy quét), hoặc
//   (b) chuỗi nhảy một phát ≥ minLength ký tự (dán từ clipboard), hoặc
//   (c) chuỗi toàn số và đủ độ dài IMEI (15) — gõ tay đủ IMEI cũng khỏi Enter.
// Gõ tay lai rai vài ký tự (ví dụ alias 6 số cuối) KHÔNG kích hoạt — vẫn Enter
// hoặc bấm nút như cũ, tránh gửi chuỗi dở dang rồi báo "không tìm thấy".
//
// Phần quyết định nằm ở feedCadence (hàm thuần, có test ở
// use-scanner-auto-submit.test.ts); hook chỉ lo ref + timer.
import { useEffect, useRef } from "react";

/** Khoảng cách phím tối đa (ms) vẫn coi là máy quét bắn. */
export const FAST_MS = 35;
/** Ngừng nhập bao lâu (ms) thì chốt và gửi. */
export const QUIET_MS = 140;
/** Độ dài IMEI đầy đủ — gõ tay đủ số này cũng tự gửi. */
export const FULL_IMEI_LEN = 15;
/** Độ dài tối thiểu mặc định mới tự gửi (chống gửi chuỗi dở). */
export const DEFAULT_MIN_LENGTH = 8;

/** Nhịp gõ tích luỹ của chuỗi đang nhập. */
export interface ScanCadence {
  lastTs: number;
  fastCount: number;
  prevLen: number;
  pasted: boolean;
}

export function initialCadence(): ScanCadence {
  return { lastTs: 0, fastCount: 0, prevLen: 0, pasted: false };
}

/**
 * feedCadence — nạp giá trị ô input tại thời điểm `now`, trả về nhịp mới và
 * việc có nên tự gửi hay không (sau khi người dùng ngừng nhập QUIET_MS).
 */
export function feedCadence(
  cadence: ScanCadence,
  rawText: string,
  now: number,
  minLength = DEFAULT_MIN_LENGTH,
): { next: ScanCadence; autoSubmit: boolean } {
  const text = rawText.trim();
  const len = text.length;

  // Ô trống (vừa xoá sau khi gửi, hoặc người dùng xoá tay) → quên nhịp cũ.
  if (len === 0) return { next: initialCadence(), autoSubmit: false };

  const gap = cadence.lastTs === 0 ? Infinity : now - cadence.lastTs;
  const grew = len - cadence.prevLen;

  let { fastCount, pasted } = cadence;
  if (grew >= minLength) {
    // Nhảy một phát cả cụm = dán clipboard (hoặc máy quét bắn gộp 1 sự kiện).
    pasted = true;
  } else if (gap <= FAST_MS) {
    fastCount += 1;
  } else if (gap > QUIET_MS) {
    // Người dùng ngừng rồi gõ tiếp → nhịp cũ không còn đại diện.
    fastCount = 0;
    pasted = false;
  }

  const next: ScanCadence = { lastTs: now, fastCount, prevLen: len, pasted };

  // Ký tự đầu tiên luôn đến sau một khoảng lặng nên không tính là "nhanh";
  // trừ hao 2 nhịp để chuỗi máy quét vẫn đạt ngưỡng.
  const scannerPaced = fastCount >= len - 2 && len >= minLength;
  const fullImei = /^\d+$/.test(text) && len >= FULL_IMEI_LEN;

  return { next, autoSubmit: scannerPaced || pasted || fullImei };
}

interface Options {
  /** Tắt khi ô đang khoá/đang gửi. Mặc định bật. */
  enabled?: boolean;
  /** Độ dài tối thiểu mới tự gửi. Mặc định 8. */
  minLength?: number;
}

/**
 * Gọi `submit` sau khi người dùng ngừng nhập, nếu chuỗi trông như vừa được máy
 * quét bắn vào. Truyền chính `value` của ô input; `submit` được giữ trong ref
 * nên không cần memo hoá ở phía gọi.
 */
export function useScannerAutoSubmit(
  value: string,
  submit: () => void,
  { enabled = true, minLength = DEFAULT_MIN_LENGTH }: Options = {},
) {
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const cadenceRef = useRef<ScanCadence>(initialCadence());

  useEffect(() => {
    const { next, autoSubmit } = feedCadence(cadenceRef.current, value, Date.now(), minLength);
    cadenceRef.current = next;
    if (!enabled || !autoSubmit) return;

    const timer = setTimeout(() => submitRef.current(), QUIET_MS);
    return () => clearTimeout(timer);
  }, [value, enabled, minLength]);
}
